import React, { useEffect, useRef, useState } from 'react';
import './replyStyles.scss';
import GeckoLocation from 'services/gecko-location';
import { OverlayTrigger, Tooltip } from 'ui';
import thumbTackCloseImg from '../../assets/thumbtack-close.svg';
import { pinSavedReply, unpinSavedReply } from '../../actions/saved_replies';
import { useSelector } from 'react-redux';
import { getSavedReplies } from '../../selectors/saved_replies';
import { handleAPIError } from '../../utilities/errorHelper';

function SavedReplySelector({ onSelect = () => {} }) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const selectContainer = useRef(null);
  const teamIds = useSelector((state) => state.appState.myTeamIds);
  const teams = useSelector((state) => state.teams);
  const usersTeamIds = [...teamIds].sort((a, b) => {
    const aUpper = teams.find((team) => team.id === a).name.toUpperCase();
    const bUpper = teams.find((team) => team.id === b).name.toUpperCase();
    if (aUpper < bUpper) return -1;
    if (aUpper > bUpper) return 1;
    return 0;
  });

  const [pinRequestInProgress, setPinRequestInProgress] = useState(false);
  const savedReplies = useSelector(getSavedReplies);

  const globalIdentifier = 'global';
  const pinnedIdentifier = 'pinned';

  const filterBySearchTerm = (searchTerm) => {
    const searchTermLower = searchTerm.toLowerCase();

    return savedReplies.filter((reply) =>
      reply.name.toLowerCase().includes(searchTermLower)
    );
  };

  useEffect(() => {
    const mouseDownHandler = (event) => {
      // If the selector isn't open or we don't have a handle on it's container return early
      if (!open || selectContainer.current === null) {
        return;
      }
      // If the click was inside the container return early
      if (selectContainer.current.contains(event.target)) {
        return;
      }
      // Otherwise close the selector
      setOpen(false);
    };
    window.addEventListener('mousedown', mouseDownHandler);
    return () => {
      window.removeEventListener('mousedown', mouseDownHandler);
    };
  }, [open]);
  const updateOptions = (term) => {
    const replies = filterBySearchTerm(term);
    setResults(replies);
  };

  const closeSelector = () => {
    // set timeout here to pop the closing of the selector off the queue to allow time for any click events to fire
    setTimeout(() => {
      setOpen(false);
      setSearchTerm('');
      setResults([]);
    }, 150);
  };

  const openSelector = () => {
    if (open) {
      return closeSelector();
    }
    setOpen(true);
    setSearchTerm('');
    updateOptions('');
  };

  const onSearchChange = (event) => {
    setSearchTerm(event.target.value);
    updateOptions(event.target.value);
  };

  const select = (value) => {
    closeSelector();
    onSelect(value);
  };

  const hasSavedReplyForSearchTerm = () => results && results.length;

  const renderSearchBar = () => (
    <div className="dropdown-search">
      <i className="far fa-search"></i>
      <input
        autoFocus
        type="text"
        placeholder="Search saved replies"
        value={searchTerm}
        onChange={onSearchChange}
      />
    </div>
  );

  const handlePinSavedReply = async (savedReply) => {
    if (pinRequestInProgress) {
      return;
    }

    setPinRequestInProgress(true);

    try {
      await pinSavedReply(savedReply);
      setPinRequestInProgress(false);
    } catch (err) {
      setPinRequestInProgress(false);
      handleAPIError(err, 'Could not pin Saved Reply');
    }
  };

  const handleUnpinSavedReply = async (savedReply) => {
    if (pinRequestInProgress) {
      return;
    }

    setPinRequestInProgress(true);

    try {
      await unpinSavedReply(savedReply);
      setPinRequestInProgress(false);
    } catch (err) {
      setPinRequestInProgress(false);
      handleAPIError(err, 'Could not unpin Saved Reply');
    }
  };

  const renderPinButton = (savedReply) => (
    <button
      className={`gecko-btn btn btn-sm btn-pin icon-only ${
        pinRequestInProgress ? 'disabled' : ''
      }`}
      onClick={
        savedReply.pinned
          ? () => handleUnpinSavedReply(savedReply)
          : () => handlePinSavedReply(savedReply)
      }
    >
      {savedReply.pinned ? (
        <span className={'pin-img-wrap'}>
          <img className={'pin-img'} src={thumbTackCloseImg} />
        </span>
      ) : (
        <i className="fal fa-thumbtack" />
      )}
    </button>
  );
  const renderSavedReply = (grouping, savedReply) => (
    <li className="dropdown-item" key={`${savedReply.id}-${grouping}`}>
      <div className={'d-flex justify-content-between'}>
        <span className={'reply-text'} onClick={() => select(savedReply)}>
          {savedReply.name}
        </span>
        {renderPinButton(savedReply)}
      </div>
    </li>
  );

  const renderGroupingHeader = (header, identifier) => (
    <li
      className="dropdown-header"
      key={`${header}-${identifier}`}
      onClick={() => {}}
    >
    {header}
    </li>
  );

  const renderSavedReplyList = () => {
    const sortGroupedResults = (groupedReplies) => {
      groupedReplies.sort((a, b) => {
        const aUpper = a.name.toUpperCase();
        const bUpper = b.name.toUpperCase();

        if (aUpper < bUpper) {
          return -1;
        }
        if (aUpper > bUpper) {
          return 1;
        }
        return 0;
      });

      return groupedReplies;
    };

    const getTeamIdsOrGroupIdentifier = (savedReply) => {
      if (savedReply.pinned) {
        return [pinnedIdentifier];
      }

      if (savedReply.assigned_team_ids?.length > 0) {
        return savedReply.assigned_team_ids;
      }

      return [globalIdentifier];
    };
    const replies = filterBySearchTerm(searchTerm);
    const seperatedResults = replies.reduce(
      (groupedSavedReplies, savedReply) => {
        const identifiers = getTeamIdsOrGroupIdentifier(savedReply);

        identifiers.forEach((groupIdentifier) => {
          const savedRepliesForId = groupedSavedReplies[groupIdentifier];
          const updatedSavedRepliesForId = Array.isArray(savedRepliesForId)
            ? [...savedRepliesForId, savedReply]
            : [savedReply];
          groupedSavedReplies[groupIdentifier] = sortGroupedResults(
            updatedSavedRepliesForId
          );
        });
        return groupedSavedReplies;
      },
      {}
    );

    // get pinned replies
    // get users teams, get saved replies for each team + global team
    const groupingIdentifiers = [
      pinnedIdentifier,
      ...usersTeamIds,
      globalIdentifier,
    ];

    return groupingIdentifiers.flatMap((identifier) => {
      let sectionHeader;
      if (identifier === globalIdentifier) {
        sectionHeader = 'Shared replies';
      } else if (identifier === pinnedIdentifier) {
        sectionHeader = 'Pinned';
      } else {
        sectionHeader = teams.find((team) => team.id === identifier).name;
      }

      return seperatedResults[identifier]
        ? [
            renderGroupingHeader(sectionHeader, identifier),
            ...seperatedResults[identifier].map(
              renderSavedReply.bind(this, sectionHeader)
            ),
          ]
        : [];
    });
  };

  const renderNoSavedRepliesMessage = () => (
    <li className="dropdown-item" onClick={() => GeckoLocation.goTo('/settings/savedreplies')}>
      No saved replies found,{' '}
      <strong style={{ fontWeight: 600 }}>add one</strong>.
    </li>
  );

  const renderSelector = () => {
    if (!open) {
      return null;
    }
    return (
      <div ref={selectContainer} className="gecko-dropdown-menu">
        {renderSearchBar()}
        <ul>
          {hasSavedReplyForSearchTerm()
            ? renderSavedReplyList()
            : renderNoSavedRepliesMessage()}
        </ul>
      </div>
    );
  };

  return (
    <div className={`saved-reply-selector ${open ? 'open' : null}`}>
      {renderSelector()}
      <span className="create-selector-toggle" onClick={openSelector}>
        <OverlayTrigger
          placement="top"
          delay={{ show: 0, hide: 0 }}
          overlay={<Tooltip id={'saved-reply-icon'}>Use a Saved Reply</Tooltip>}
        >
          <i className="far fa-bookmark" id={'saved-reply-icon'}></i>
        </OverlayTrigger>
      </span>
    </div>
  );
}

export default SavedReplySelector;
